/*
 * BLT frame sequence — the LOGIC layer for the scroll-scrubbed canvas animation
 * (paired with snippets/blt-frame-sequence.liquid, the IMAGE-SOURCE layer).
 *
 * Frame-source-agnostic: for every [data-blt-scene] on the page it reads a JSON array
 * of frame image URLs from that scene's [data-seq-frames], preloads them, and as
 * you scroll the tall scene section it (1) scrubs the frame drawn to <canvas>,
 * (2) grows the shoe slightly, (3) fades the hero head/foot text, and (4) reveals
 * the two-phase edge callout labels within their scroll windows. Swap the snippet's
 * frame URLs and the scrub is unchanged — but the callout/phase reveal in (4) is
 * NMS-specific (keyed on .blt-nms-callouts and its data-p1/p2-in/out windows).
 *
 * Idempotent: each scene is initialised at most once (data-blt-scene-init) and the
 * global scroll/resize listeners are bound at most once, so loading this script
 * more than once (e.g. a section rendered twice) is harmless.
 */
(function () {
  if (window.__bltFrameSeqBound) {
    // Already loaded — just (re)scan for any not-yet-initialised scenes.
    initAll();
    return;
  }
  window.__bltFrameSeqBound = true;

  var scenes = [];

  function clamp(v, a, b) { return Math.min(b, Math.max(a, v)); }

  function initScene(el) {
    if (el.dataset.bltSceneInit) return;
    el.dataset.bltSceneInit = '1';

    var canvas = el.querySelector('.blt-nms-scene__canvas');
    var json = el.querySelector('[data-seq-frames]');
    if (!canvas || !json) return;
    var urls;
    try { urls = JSON.parse(json.textContent); } catch (e) { return; }
    if (!urls.length) return;

    var ctx = canvas.getContext('2d');
    var images = new Array(urls.length);
    var current = -1;
    var callouts = Array.prototype.slice.call(el.querySelectorAll('.blt-nms-callout'));
    // Two-phase callout reveal: the container carries the two scroll windows (phase 1 =
    // shoe toe-left, phase 2 = toe-right); each flips the label layout. Read once.
    var calloutBox = el.querySelector('.blt-nms-callouts');
    var p1In, p1Out, p2In, p2Out;
    if (calloutBox) {
      p1In = parseFloat(calloutBox.dataset.p1In);
      p1Out = parseFloat(calloutBox.dataset.p1Out);
      p2In = parseFloat(calloutBox.dataset.p2In);
      p2Out = parseFloat(calloutBox.dataset.p2Out);
    }
    var lastPhase = -1;

    function draw(i) {
      var img = images[i];
      if (!img || !img.complete || !img.naturalWidth) return;
      // Frames may be transparent (PNG/AVIF with alpha) — clear first so successive
      // frames don't ghost/trail over each other.
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    }

    function render() {
      var rect = el.getBoundingClientRect();
      var total = rect.height - window.innerHeight;
      var p = total > 0 ? clamp(-rect.top / total, 0, 1) : 0;

      // 1) scrub frame
      var frame = Math.min(urls.length - 1, Math.round(p * (urls.length - 1)));
      if (frame !== current) { current = frame; draw(frame); }

      // shoe stays fixed at center (only a very slight grow); text fades away
      el.style.setProperty('--scene-scale', (1 + p * 0.12).toFixed(3));
      el.style.setProperty('--scene-head-op', clamp(1 - p * 2.6, 0, 1).toFixed(3));
      el.style.setProperty('--scene-foot-op', clamp(1 - p * 2.6, 0, 1).toFixed(3));

      // reveal callout labels: two-phase. Phase 1/2 flip the left-right layout (is-p1/is-p2)
      // because the shoe rotates ~180° as it splits; outside both windows all labels fade out.
      // Only touch the DOM when the phase changes (mirrors the frame !== current guard above).
      if (calloutBox) {
        var ph = (p >= p1In && p <= p1Out) ? 1 : (p >= p2In && p <= p2Out) ? 2 : 0;
        if (ph !== lastPhase) {
          lastPhase = ph;
          // Keep the last phase's layout class through the fade-out (ph === 0) so the labels
          // fade IN PLACE. Clearing it would snap them to their unpositioned default (top-left)
          // for the 0.5s fade, flashing stray connector lines during the transition. Only swap
          // the layout when actively entering a phase; is-in alone drives show/hide.
          if (ph === 1) { calloutBox.classList.add('is-p1'); calloutBox.classList.remove('is-p2'); }
          else if (ph === 2) { calloutBox.classList.add('is-p2'); calloutBox.classList.remove('is-p1'); }
          for (var c = 0; c < callouts.length; c++) callouts[c].classList.toggle('is-in', ph > 0);
        }
      }
    }

    urls.forEach(function (url, i) {
      var img = new Image();
      img.onload = function () {
        if (i === 0 && current < 0) draw(0);
        else if (i === current) draw(current);
      };
      img.src = url;
      images[i] = img;
    });

    scenes.push({ render: render });
    render();
  }

  function initAll() {
    document.querySelectorAll('[data-blt-scene]').forEach(initScene);
  }

  var ticking = false;
  function renderAll() {
    ticking = false;
    for (var i = 0; i < scenes.length; i++) scenes[i].render();
  }
  function onScroll() {
    if (!ticking) { ticking = true; requestAnimationFrame(renderAll); }
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
